import { DRONE_MAX_FLEET, DRONE_SHOP_ITEM_ID } from '../../shared/constants.js';
import type { FacilityActionPayload, RoomState } from '../../shared/types.js';
import type { ControllerNetwork } from '../controllerNetwork.js';
import { state as controllerState } from '../controllerState.js';
import { buildTechTreeIndex, type TechTreeData, type TechTreeIndex } from '../../shared/techTree.js';
import { htmlEl } from './stationDisplays.js';
import { createChipTabs, createFacilityHead, createSearchBar } from './facilityUi.js';
import {
  SHOP_FILTER_CHIPS,
  createShopFilterState,
  matchesShopFilter,
  type ShopFilterId,
  type ShopItemKind
} from '../../shared/shopFilter.js';

type Options = {
  controls: HTMLDivElement | null;
  getControllerNetwork: () => ControllerNetwork | null;
};

type ShopMode = 'buy' | 'sell';

const MAX_BUY_QUANTITY = 99;

let techIndex: TechTreeIndex | null = null;
let techLoadStarted = false;

const loadTechIndex = (onReady: () => void): void => {
  if (techIndex) { onReady(); return; }
  if (techLoadStarted) return;
  techLoadStarted = true;
  fetch('/config/tech-tree-data.json')
    .then((res) => res.json() as Promise<TechTreeData>)
    .then((data) => { techIndex = buildTechTreeIndex(data); onReady(); })
    .catch(() => { techLoadStarted = false; });
};

export const createFacilityShopController = ({ controls, getControllerNetwork }: Options) => {
  let list: HTMLDivElement | null = null;
  let setCredits: ((value: number) => void) | null = null;
  let mode: ShopMode = 'buy';
  let tabs: ReturnType<typeof createChipTabs<ShopMode>> | null = null;
  let builtSignature = '';
  const quantities = new Map<string, number>();
  const filterState = createShopFilterState();
  let filterTabs: ReturnType<typeof createChipTabs<ShopFilterId | 'hold'>> | null = null;

  const emit = (payload: FacilityActionPayload): void => {
    getControllerNetwork()?.facilityAction(payload);
  };

  const droneCount = (): number => controllerState.lastSnapshot?.drones?.length ?? 0;
  const held = (id: string): number => id === DRONE_SHOP_ITEM_ID ? droneCount() : (controllerState.lastRoomState?.cargo[id] ?? 0);
  const credits = (): number => controllerState.lastRoomState?.credits ?? 0;

  const itemName = (id: string): string => id === DRONE_SHOP_ITEM_ID ? 'Survey Drone (ROV)' : (techIndex?.nodes[id]?.name ?? id);

  const kindOf = (id: string): ShopItemKind => {
    if (id === DRONE_SHOP_ITEM_ID) return 'drone';
    const node = techIndex?.nodes[id];
    if (!node) return 'other';
    if (node.type === 'product') return node.kind === 'upgrade' ? 'upgrade' : 'consumable';
    return node.type;
  };

  const allRowsFor = (roomState: RoomState, shopMode: ShopMode): string[] => {
    if (!techIndex) return [];
    const prices = roomState.facilityPrices;
    if (shopMode === 'buy') {
      const ids: string[] = [];
      for (const id of Object.keys(techIndex.data.refined)) if (prices[`buy_${id}`] !== undefined) ids.push(id);
      for (const [id, def] of Object.entries(techIndex.data.products)) {
        if (def.kind === 'consumable' && prices[`buy_${id}`] !== undefined) ids.push(id);
      }
      if (prices[`buy_${DRONE_SHOP_ITEM_ID}`] !== undefined) ids.push(DRONE_SHOP_ITEM_ID);
      return ids.sort((a, b) => itemName(a).localeCompare(itemName(b)));
    }
    return Object.entries(roomState.cargo)
      .filter(([id, qty]) => qty > 0 && prices[`sell_${id}`] !== undefined)
      .map(([id]) => id)
      .sort((a, b) => itemName(a).localeCompare(itemName(b)));
  };

  const rowsFor = (roomState: RoomState, shopMode: ShopMode): string[] =>
    allRowsFor(roomState, shopMode).filter((id) => matchesShopFilter({ name: itemName(id), kind: kindOf(id), held: held(id) }, filterState));

  const refreshRow = (row: HTMLElement): void => {
    const id = row.dataset.id as string;
    const rowMode = row.dataset.mode as ShopMode;
    const roomState = controllerState.lastRoomState;
    if (!roomState) return;
    const price = roomState.facilityPrices[`${rowMode}_${id}`] ?? 0;
    const isDrone = id === DRONE_SHOP_ITEM_ID;
    const freeDroneSlots = Math.max(0, DRONE_MAX_FLEET - droneCount());
    const limit = rowMode === 'sell' ? Math.max(1, held(id)) : isDrone ? Math.max(1, freeDroneSlots) : MAX_BUY_QUANTITY;
    const qty = Math.max(1, Math.min(quantities.get(`${rowMode}_${id}`) ?? 1, limit));
    quantities.set(`${rowMode}_${id}`, qty);

    (row.querySelector('.fc-qty') as HTMLElement).textContent = String(qty);
    (row.querySelector('.fc-held') as HTMLElement).textContent = isDrone ? `fleet: ${droneCount()} of ${DRONE_MAX_FLEET}` : `in hold: ${held(id)}`;
    const total = price * qty;
    const button = row.querySelector<HTMLButtonElement>('.fc-trade')!;
    button.textContent = `${rowMode === 'buy' ? 'Buy' : 'Sell'} · ${total.toLocaleString()} CR`;
    button.disabled = rowMode === 'buy' ? (roomState.credits < total || (isDrone && freeDroneSlots === 0)) : held(id) < qty;
    if (isDrone && freeDroneSlots === 0) button.textContent = 'Fleet full';
  };

  const buildRow = (id: string, rowMode: ShopMode, roomState: RoomState): HTMLElement => {
    const price = roomState.facilityPrices[`${rowMode}_${id}`] ?? 0;
    const row = htmlEl('div', 'facility-item-row fc-row');
    row.dataset.id = id;
    row.dataset.mode = rowMode;

    const info = htmlEl('div', 'fc-row-info');
    info.append(htmlEl('div', 'fc-card-name', itemName(id)), htmlEl('div', 'fc-row-meta', `${price.toLocaleString()} CR each`), htmlEl('div', 'fc-held fc-row-meta'));

    const stepper = htmlEl('div', 'fc-stepper');
    const minus = htmlEl('button', 'facility-qty-btn', '−');
    minus.type = 'button';
    const qty = htmlEl('span', 'fc-qty facility-qty-display', '1');
    const plus = htmlEl('button', 'facility-qty-btn', '+');
    plus.type = 'button';
    stepper.append(minus, qty, plus);

    const trade = htmlEl('button', `fc-trade ${rowMode === 'buy' ? 'facility-buy-btn' : 'facility-sell-btn'}`);
    trade.type = 'button';

    const bump = (delta: number): void => {
      quantities.set(`${rowMode}_${id}`, (quantities.get(`${rowMode}_${id}`) ?? 1) + delta);
      refreshRow(row);
    };
    minus.addEventListener('click', () => bump(-1));
    plus.addEventListener('click', () => bump(1));
    trade.addEventListener('click', () => {
      const code = controllerState.currentRoomCode;
      if (!code) return;
      const quantity = quantities.get(`${rowMode}_${id}`) ?? 1;
      emit(rowMode === 'buy'
        ? { code, type: 'buy_item', itemId: id, quantity }
        : { code, type: 'sell_item', itemId: id, quantity });
    });

    row.append(info, stepper, trade);
    refreshRow(row);
    return row;
  };

  const syncedOn = (): Array<ShopFilterId | 'hold'> => (filterState.inHoldOnly ? [filterState.filter, 'hold'] : [filterState.filter]);

  const signatureFor = (shopMode: ShopMode, ids: string[]): string =>
    `${shopMode}:${filterState.filter}:${filterState.searchText}:${filterState.inHoldOnly}:${ids.join(',')}`;

  const buildList = (): void => {
    const roomState = controllerState.lastRoomState;
    if (!list || !roomState || !techIndex) return;
    list.innerHTML = '';
    const ids = rowsFor(roomState, mode);
    builtSignature = signatureFor(mode, ids);
    if (ids.length === 0) {
      const filtered = filterState.searchText.trim() !== '' || filterState.filter !== 'all' || filterState.inHoldOnly;
      list.append(htmlEl('div', 'facility-placeholder', filtered
        ? 'Nothing matches. Clear the search or pick another filter.'
        : mode === 'sell' ? 'Nothing in the hold can be sold here.' : 'Nothing for sale.'));
      return;
    }
    for (const id of ids) list.append(buildRow(id, mode, roomState));
  };

  const render = (): void => {
    if (!controls) return;
    controls.innerHTML = '';
    controls.classList.add('facility-role-layout');

    const head = createFacilityHead('BASE SHOP');
    setCredits = head.setCredits;
    head.setCredits(credits());

    tabs = createChipTabs<ShopMode>({
      tabs: [{ id: 'buy', label: 'Buy' }, { id: 'sell', label: 'Sell' }],
      active: mode,
      onChange: (id) => {
        mode = id;
        buildList();
      }
    });

    const search = createSearchBar('Search items…', (text) => {
      filterState.searchText = text;
      buildList();
    });
    search.input.value = filterState.searchText;

    filterTabs = createChipTabs<ShopFilterId | 'hold'>({
      tabs: [...SHOP_FILTER_CHIPS.map((chip) => ({ id: chip.id as ShopFilterId | 'hold', label: chip.label })), { id: 'hold', label: 'In hold' }],
      active: filterState.filter,
      onChange: (id) => {
        if (id === 'hold') {
          filterState.inHoldOnly = !filterState.inHoldOnly;
        } else {
          filterState.filter = id;
        }
        filterTabs?.setOn(syncedOn());
        buildList();
      }
    });
    filterTabs.root.classList.add('fc-filter-chips');
    filterTabs.setOn(syncedOn());

    list = htmlEl('div', 'fc-list fc-rows');
    controls.append(head.root, tabs.root, search.root, filterTabs.root, list);
    builtSignature = '';
    loadTechIndex(buildList);
  };

  const updatePanel = (): void => {
    const roomState = controllerState.lastRoomState;
    if (!roomState || !list || !techIndex) return;
    setCredits?.(roomState.credits);
    const ids = rowsFor(roomState, mode);
    if (signatureFor(mode, ids) !== builtSignature) {
      buildList();
      return;
    }
    for (const row of list.querySelectorAll<HTMLElement>('.fc-row')) refreshRow(row);
  };

  return { render, updatePanel };
};
